'use client';

import { c as _c } from "react/compiler-runtime";
import { type Post } from '@shared/all/domains/posts';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2, Minus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import useContentUIRenderer from '../../../hooks/useContentUIRenderer';
import { client } from '../../../utils/trpc';
import Error from '../../common/Error';
import Modal, { ModalContent, ModalHeader } from '../../HOC/Modal';
import PostDeleteConf from '../PostDeleteConf';
import PostItemBody from '../PostItemBody';
import type { DraftItemSelectHandler } from './NewPostModal';
type Props = {
  closeHandler: () => void;
  draftItemSelectHandler: (draftItemSelectHandler: DraftItemSelectHandler) => void;
};
const DraftPostsList = props => {
  const $ = _c(39);
  const {
    ref,
    inView
  } = useInView();
  const {
    filterText
  } = useContentUIRenderer();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["drafts"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: t0,
      queryFn: _temp,
      initialPageParam: undefined as string | undefined,
      getNextPageParam: _temp2
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    error
  } = useInfiniteQuery(t1);
  let t2;
  if ($[2] !== fetchNextPage || $[3] !== inView) {
    t2 = () => {
      if (inView) {
        fetchNextPage();
      }
    };
    $[2] = fetchNextPage;
    $[3] = inView;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== inView) {
    t3 = [inView];
    $[5] = inView;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h2>Drafts</h2>;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== props.closeHandler) {
    t5 = <ModalHeader closeHandler={props.closeHandler}>{t4}</ModalHeader>;
    $[8] = props.closeHandler;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== data?.pages || $[11] !== error || $[12] !== filterText || $[13] !== isLoading || $[14] !== props) {
    t6 = !isLoading && !error && data?.pages.flatMap(_temp3).map(post => <DraftPostItem key={post.id} post={post} clickHandler={() => props.draftItemSelectHandler({
      content: filterText({
        content: post.content,
        isFullLength: true
      }),
      factiiiOpen: post.postFactiiisCount.length > 0,
      refDraftPostId: post.id,
      anonymousPost: post.user.anonymous
    })} />);
    $[10] = data?.pages;
    $[11] = error;
    $[12] = filterText;
    $[13] = isLoading;
    $[14] = props;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== data?.pages || $[17] !== error || $[18] !== isLoading) {
    t7 = !isLoading && !error && data?.pages.flatMap(_temp4).length === 0 && <p className="m-2 text-center text-textPrimary">No drafts found</p>;
    $[16] = data?.pages;
    $[17] = error;
    $[18] = isLoading;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== data?.pages || $[21] !== hasNextPage) {
    t8 = data?.pages.flatMap(_temp5).length !== 0 && !hasNextPage && <p className="m-2 text-center text-textPrimary">{"You've reached the end. \uD83C\uDF89"}</p>;
    $[20] = data?.pages;
    $[21] = hasNextPage;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== hasNextPage || $[24] !== ref) {
    t9 = hasNextPage && <div className="m-2 flex justify-center text-textPrimary" ref={ref}><Loader2 className="animate-spin" height={50} width={50} /></div>;
    $[23] = hasNextPage;
    $[24] = ref;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== error || $[27] !== isLoading) {
    t10 = <Error error={error} loading={isLoading} />;
    $[26] = error;
    $[27] = isLoading;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t11 = <ModalContent>{t6}{t7}{t8}{t9}{t10}</ModalContent>;
    $[29] = t10;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== props.closeHandler || $[36] !== t11 || $[37] !== t5) {
    t12 = <Modal closeHandler={props.closeHandler}>{t5}{t11}</Modal>;
    $[35] = props.closeHandler;
    $[36] = t11;
    $[37] = t5;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
type DraftPostItemProps = {
  post: Post;
  clickHandler: () => void;
};
function DraftPostItem(props) {
  const $ = _c(5);
  const [deletePostConfOpen, setDeletePostConfOpen] = useState(false);
  const [discarded, setDiscarded] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = discarded_0 => {
      setDiscarded(discarded_0);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const discardPost = t0;
  let t1;
  if ($[1] !== deletePostConfOpen || $[2] !== discarded || $[3] !== props) {
    t1 = discarded ? null : <div className="flex items-center justify-between rounded-md px-2 text-start hover:bg-background-light"><div className="flex-1 cursor-pointer" onClick={() => props.clickHandler()}><PostItemBody post={props.post} nsfw={false} politics={false} troll={false} disableLink={false} isFullLength={false} isInView={true} ignorePostFactiiiIds={[]} /><span className="text-sm text-textSecondary/60">Saved on {new Date(props.post.createdAt).toLocaleDateString()}{" "}{new Date(props.post.createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
          })}</span></div><button onClick={() => setDeletePostConfOpen(true)} className="flex items-center justify-center p-2 text-destructive hover:text-destructive"><Minus /></button>{deletePostConfOpen && <PostDeleteConf closeHandler={() => setDeletePostConfOpen(false)} postId={props.post.id} isDraft={true} discardPost={discardPost} draftContent={props.post.content} />}</div>;
    $[1] = deletePostConfOpen;
    $[2] = discarded;
    $[3] = props;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
export default DraftPostsList;
function _temp(t0) {
  const {
    pageParam
  } = t0;
  return unwrapFromResult(client.posts.drafts.query({
    cursor: pageParam
  }));
}
function _temp2(lastPage) {
  const posts = lastPage.posts;
  if (posts.length < 10) {
    return;
  }
  return posts[posts.length - 1]?.id;
}
function _temp3(page) {
  return page.posts;
}
function _temp4(page_0) {
  return page_0.posts;
}
function _temp5(page_1) {
  return page_1.posts;
}